import React, { useState, useEffect } from "react";
import ReactGA from "react-ga";
import styled from "@emotion/styled/macro";

import { IUser, getPrizeImage } from "../ApiService";
import { IBobblehead } from "../App";
import ModelViewer, { ModelState } from "./ModelViewer";

import SignInDialog from "../Dialogs/SignInDialog";

import checkmarkIcon from "../res/i_checkmark.svg";
import BobbleFooter from "../common/BobbleFooter";
import IBobbleViewProps from "./IBobbleViewProps";

type Props = IBobbleViewProps & {
  bobblehead: IBobblehead;
  dataInitialized: boolean;
  hasBobbleheads: boolean;
  hasBobbleheadInCollection: boolean;
  isSigningIn: boolean;
  onGetMyBonusSuccess: (email: string) => void;
  signedInUser: IUser | null;
  showSignInDialog: () => void;
};

const Root = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Background = styled<"div", { primaryLogoUrl: string }>("div")`
  position: absolute;
  width: 80%;
  background: url(${({ primaryLogoUrl }) => primaryLogoUrl}) no-repeat center
    center;
  background-size: contain;
  opacity: 0.3;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -62.5%);

  &:before {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`;

const Added = styled("div")`
  padding-left: 23px;
  background: url(${checkmarkIcon}) no-repeat left center;
  background-size: 16px;
  margin: 0 auto;
  font-size: 12px;
`;

export default function BobbleContent({
  bobblehead,
  dataInitialized,
  hasBobbleheads,
  hasBobbleheadInCollection,
  isScratched,
  isSigningIn,
  onGetMyBonusSuccess,
  onModelRotated = () => {},
  signedInUser,
  showSignInDialog,
  ...rest
}: Props) {
  const {
    primaryLogoUrl,
    modelUrl,
    scratchCoverUrl,
    baseTextureUrl,
    baseSideTextureUrl,
    modelScaleFactor,
    modelOffsetY,
    baseScaleFactor,
    baseOffsetY
  } = bobblehead;
  const [modelState, setModelState] = useState<ModelState>("Loading");
  const [isStartingMyCollection, setIsStartingMyCollection] = useState(false);
  const [isGettingMyBonus, setIsGettingMyBonus] = useState(false);
  const [isShowingEventInfo, setIsShowingEventInfo] = useState(false);
  const isSignedIn = !!signedInUser;

  useEffect(() => {
    const collected = dataInitialized ? hasBobbleheadInCollection : false;
    const bonus = dataInitialized ? isScratched : false;
    ReactGA.pageview(
      `/bobbleviewer/bobble=${bobblehead.id}/signedIn=${isSignedIn}/collected=${collected}/bonus=${bonus}`
    );
  }, [
    isSignedIn,
    dataInitialized,
    hasBobbleheadInCollection,
    isScratched,
    bobblehead.id
  ]);

  return (
    <Root>
      <Background primaryLogoUrl={primaryLogoUrl} />
      <ModelViewer
        {...rest}
        interactionsEnabled={
          !isSigningIn && !isStartingMyCollection && !isGettingMyBonus
        }
        isScratched={isScratched}
        showButtons={!!signedInUser}
        modelPath={modelUrl}
        modelState={modelState}
        rewardPath={getPrizeImage(signedInUser, bobblehead)}
        scratchImagePath={scratchCoverUrl}
        baseTexturePath={baseTextureUrl}
        cubeSidePath={baseSideTextureUrl}
        changeModelState={setModelState}
        modelOffsetY={modelOffsetY}
        baseOffsetY={baseOffsetY}
        modelScaleFactor={modelScaleFactor}
        baseScaleFactor={baseScaleFactor}
        onModelRotated={() => {
          onModelRotated();
          if (!signedInUser) {
            setModelState("Default");
            showSignInDialog();
          }
        }}
        onBaseClicked={() => {

        }}
        onScratch={() => {
          if(signedInUser && signedInUser.email) {
            return;
          }
          if (modelState === "Reveal") {
            setIsGettingMyBonus(true);
          }
        }}       
      />
      <BobbleFooter
        mainComponent={
          signedInUser ? (
            hasBobbleheads ? (
              hasBobbleheadInCollection ? (
                <Added>Added to My Collection</Added>
              ) : null
            ) : null
          ) : null
        }
        showEventInfo={() => setIsShowingEventInfo(true)}
      />
      {signedInUser && isGettingMyBonus && (
        <SignInDialog
          primaryLogoUrl={bobblehead ? bobblehead.primaryLogoUrl : null}
          onClose={() => setIsGettingMyBonus(false)}
          onSuccess={(response) => {
            onGetMyBonusSuccess(response.email);
            setIsGettingMyBonus(false);
          }}
        />
      )}
    </Root>
  );
}
